'use client';

import { Button, ButtonGroup, Dialog, Text } from '@/_components';
import { useCallback, useContext, useState, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { restoreDeletedQuestionnaire } from '@/_api/admin-questionnaires';
import type { RestoreQuestionnaireDialogProps } from './RestoreQuestionnaireDialog.types';

export const RestoreQuestionnaireDialog: FC<
  RestoreQuestionnaireDialogProps
> = ({ isOpen, onClose, selectedQuestionnaires }) => {
  const { token } = useContext(UserContext);
  const { loadQuestionnaires } = useContext(QuestionnaireContext);
  const { addToast } = useContext(ToastContext);
  const [isRestoring, setIsRestoring] = useState(false);

  const onRestore = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setIsRestoring(true);
      await Promise.all(
        selectedQuestionnaires.map((questionnaire) =>
          restoreDeletedQuestionnaire({
            questionnaire_id: questionnaire.id,
            token,
          }),
        ),
      );
      addToast('success', 'Questionnaires restored successfully');
    } catch (error) {
      addToast('error', 'Failed to restore questionnaires', { error });
    } finally {
      loadQuestionnaires();
      setIsRestoring(false);
      onClose();
    }
  }, [selectedQuestionnaires, token, loadQuestionnaires, onClose, addToast]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Restore Questionnaires">
      <Dialog.Content>
        {selectedQuestionnaires.length === 1 ? (
          <Text>Are you sure you want to restore this questionnaire?</Text>
        ) : (
          <Text>
            Are you sure you want to restore {selectedQuestionnaires.length}{' '}
            selected questionnaires?
          </Text>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="ghost"
            value="Cancel"
            onClick={onClose}
            isDisabled={isRestoring}
          />
          <Button
            intent="primary"
            value="Restore"
            onClick={onRestore}
            isLoading={isRestoring}
            isDisabled={isRestoring}
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
